// The Sun as a camera photographs it, drawn by the CinematicShader pass in
// src/main.js (updateLensFlare sets the uniforms). Everything here is light
// the lens spreads round the Sun, added in linear light before the ACES tone
// curve, and the curve is what turns the brightest of it white. So the white
// core is round and soft-edged, and where a ray leaves it the ray is still
// bright enough to clip across a width that narrows with distance: core and
// rays read as one white splat with pointed arms, then the rays go on as thin
// lines, as in photographs (Apollo 11 and 12, Gemini 12).
//
// The parts, centred on the light-weighted centre of the part of the disc
// that shows (a Sun half behind a limb flares from its visible half):
//   - the core, the lens's point-spread function (1 + (th/a)^2)^-1.5, kept
//     off the body cutting the disc (lensFlareBody), and through the
//     Earth's air measured from where each pixel's light left the Sun
//     (sgSourceUv), so a Sun on the limb is lifted and squashed as the air
//     bends it;
//   - a warm glow round it and a wide, faint veil (scatter in the lens);
//   - diffraction rays from the aperture (twenty by default, lensFlareRayCount),
//     fixed to the frame as a camera's are, each falling as th^-2 along
//     its length; across it a narrow profile (the line) on a broad one (the
//     root, which clips white only near the core, so the arms start wide and
//     come to points);
//   - a fan of fine strands between them, each its own length, strength,
//     width and a little off its even place: the many needles round a bright
//     light in the eye (the ciliary corona, Ritschel et al. 2009, "Temporal
//     Glare") and in a lens with dust and scratches, which SpaceEngine draws
//     too;
//   - a thin horizontal streak, as an anamorphic lens draws;
//   - ghosts of the aperture on the line from the Sun through the middle of
//     the frame, so they swing round as the view turns.
// th is in the glare's own degrees: the view's degrees over lensFlareScale,
// which enlarges the whole photograph round a disc drawn larger than the
// Sun looks (artistic mode) so it keeps the proportions it has round the
// true Sun. A disc large enough to show its surface (lensFlareDisc.y below
// 1) keeps the glow round its limb and loses the core, rays and streak.
// Needs lensFlareSun (the glare's centre), lensFlareAspect, tDiffuse and
// cinematicExposed / cinematicGraded (the pass's tone steps) from the pass.
export const SUN_GLARE_GLSL = /* glsl */`
uniform vec2 lensFlareDiscUv;
uniform vec2 lensFlareDisc;
uniform float lensFlareSpace;
uniform float lensFlareDegPerUv;
uniform float lensFlareViewPx;
uniform float lensFlareScale;
uniform float lensFlareFarScale;
uniform float lensFlareGlowScale;
uniform float lensFlareGain;
uniform vec2 lensFlareGlow;
uniform float lensFlareLimbGlow;
uniform float lensFlareGlowEnd;
uniform float lensFlareSpikes;
uniform float lensFlareSpikeLength;
uniform float lensFlareRayCount;
uniform float lensFlareRayTurn;
uniform float lensFlareStrands;
uniform vec4 lensFlareStrandShape;
uniform vec4 lensFlareStrandMix;
uniform vec2 lensFlareStrandShift;
uniform float lensFlareStreak;
uniform float lensFlareGhosts;
uniform vec3 lensFlareTint;
uniform vec4 lensFlareShape;
uniform vec4 lensFlareRayShape;
uniform float lensFlareRootFade;
uniform vec3 lensFlareWarm;
uniform vec3 lensFlareRayTint;
uniform vec4 lensFlareBody;
uniform float lensFlareBodyMask;
uniform mat4 lensFlareInvProj;
uniform float lensFlareRefract;
uniform vec4 lensFlareAir;
uniform mat4 lensFlareProj;
uniform vec2 lensFlareSunSrc;

const float SG_PI = 3.14159265;

float sgHash(float n) {
  return fract(sin(n * 12.9898 + 4.1414) * 43758.5453);
}

// One ray: index k, angle off its axis, at th glare degrees, its line drawn
// wDraw wide with its peak lowered by lineScale. A blade edge sends its two
// rays opposite ways, so rays k and k + half the count are alike; each edge
// its own length and strength. The root is the broad part of the profile,
// bright enough to show only near the core, where it clips white.
float sgRay(float k, float off, float th, float wDraw, float lineScale) {
  float pairs = floor(lensFlareRayCount * 0.5 + 0.5);
  float edge = mod(k + 2.0 * pairs, pairs);
  float reach = 0.45 + 0.55 * sgHash(edge);
  float strength = 0.5 + 0.5 * sgHash(edge + 17.0);
  float x = th * abs(sin(off));
  float u = x / wDraw;
  float c = 1.0 + u * u;
  float r = x / lensFlareRayShape.z;
  float knee = 1.0 + th / lensFlareRayShape.y;
  float along = strength / (knee * knee) * exp(-th / (lensFlareSpikeLength * reach));
  float root = lensFlareRayShape.w / (1.0 + r * r) * exp(-th / lensFlareRootFade);
  return along * (lineScale / (c * sqrt(c)) + root);
}

// One strand, the j-th of lensFlareStrandShape.x round the light (step
// apart), at angle ang and th glare degrees. Its place is jittered by up to
// lensFlareStrandMix.x of a step; its length, in reaches of the rays' length,
// runs from lensFlareStrandMix.y of lensFlareStrandShape.y to all of it, as a
// hash to the power lensFlareStrandShape.z, so most are short and a few long;
// its strength drops by up to lensFlareStrandMix.z; its width is
// lensFlareStrandShape.w of a ray's, varied by half either way, drawn at
// least wFloor wide with the peak lowered to keep its light.
float sgStrand(float j, float ang, float th, float gap, float wRay, float wFloor) {
  float id = mod(j, lensFlareStrandShape.x);
  float at = (j + 0.5 + (sgHash(id + 3.17) - 0.5) * lensFlareStrandMix.x) * gap;
  float x = th * abs(sin(ang - at));
  float share = lensFlareStrandMix.y + (1.0 - lensFlareStrandMix.y) * pow(sgHash(id + 41.3), lensFlareStrandShape.z);
  float len = lensFlareSpikeLength * lensFlareStrandShape.y * share;
  float strength = 1.0 - lensFlareStrandMix.z * sgHash(id + 77.9);
  // The strands come from scratches and specks on the glass, and as the
  // light crosses the lens (the camera turning) it meets them differently:
  // each strand waxes and wanes, and grows and shrinks, at its own rate and
  // in its own direction across the frame (lensFlareStrandShift: how much,
  // and how many times a frame's height), keeping its mean strength and
  // length. The rays, from the aperture, stay as they are.
  if (lensFlareStrandShift.x > 0.0) {
    vec2 across = vec2(lensFlareSun.x * lensFlareAspect, lensFlareSun.y) * (2.0 * SG_PI * lensFlareStrandShift.y);
    float d1 = sgHash(id + 57.1) * 2.0 * SG_PI;
    float d2 = sgHash(id + 63.9) * 2.0 * SG_PI;
    float lit = 0.5 + 0.5 * sin(dot(across, vec2(cos(d1), sin(d1))) + sgHash(id + 91.7) * 2.0 * SG_PI);
    float grow = 0.5 + 0.5 * sin(dot(across, vec2(cos(d2), sin(d2))) + sgHash(id + 13.3) * 2.0 * SG_PI);
    float amt = lensFlareStrandShift.x;
    strength *= (1.0 - amt * (1.0 - lit)) / (1.0 - 0.5 * amt);
    len *= (1.0 - 0.6 * amt * (1.0 - grow)) / (1.0 - 0.3 * amt);
  }
  float w = wRay * lensFlareStrandShape.w * (0.5 + sgHash(id + 19.7));
  float wDraw = max(w, wFloor);
  float u = x / wDraw;
  float c = 1.0 + u * u;
  float knee = 1.0 + th / lensFlareRayShape.y;
  return strength * exp(-th / len) / (knee * knee) * (w / wDraw) / (c * sqrt(c));
}

// The aperture's outline, a regular heptagon with its corners at radius: 1
// on its edge (the distance along the nearest edge's normal over the
// apothem).
float sgAperture(vec2 p, float radius) {
  float seg = 2.0 * SG_PI / 7.0;
  float a = atan(p.y, p.x) - lensFlareRayTurn + 0.5 * seg;
  return length(p) * cos(mod(a, seg) - 0.5 * seg) / (cos(SG_PI / 7.0) * max(radius, 1e-5));
}

float sgGhost(vec2 uv, vec2 at, float radius, float soft) {
  vec2 p = vec2((uv.x - at.x) * lensFlareAspect, uv.y - at.y);
  if (dot(p, p) > radius * radius * 1.6) return 0.0;
  float d = sgAperture(p, radius);
  float fill = 1.0 - smoothstep(1.0 - soft, 1.0 + soft, d);
  // A little brighter towards the rim, as the ghosts in Apollo frames are.
  return fill * (0.62 + 0.38 * smoothstep(0.35, 1.0, d));
}

// Where the light seen at this pixel left the Sun, as a uv: a ray that
// grazes the Earth's limb at tangent height h was bent towards the ground by
// lensFlareRefract x e^(-h / 8 km) (AIR_GRAZING_BEND in src/earthclose.js,
// which the sky dome's disc follows too), so it came from that much lower.
vec2 sgSourceUv(vec2 uv) {
  vec4 nearPoint = lensFlareInvProj * vec4(uv * 2.0 - 1.0, -1.0, 1.0);
  vec3 r = normalize(nearPoint.xyz / nearPoint.w);
  vec3 c = lensFlareAir.xyz;
  float along = dot(r, c);
  if (along <= 0.0) return uv;
  float hKm = (length(cross(r, c)) / lensFlareAir.w - 1.0) * 6371.0;
  if (hKm <= 0.0 || hKm >= 120.0) return uv;
  vec3 up = normalize(along * r - c);
  vec3 source = normalize(r - up * (lensFlareRefract * exp(-hKm / 8.0)));
  vec4 clip = lensFlareProj * vec4(source, 1.0);
  return clip.xy / clip.w * 0.5 + 0.5;
}

// The frame through the pass's exposure and grade at rad from the disc's
// centre (in the glare's measure, as the limb) along dir, moved onto the
// frame where it lies off it; w is how far that point is from the centre.
vec4 sgRadialLight(vec2 dir, float rad) {
  float px = 1.0 / max(lensFlareViewPx, 1.0);
  vec2 toUv = vec2(1.0 / max(lensFlareAspect, 1e-4), 1.0);
  vec2 edge = 0.5 * px * toUv;
  vec2 at = clamp(lensFlareDiscUv + dir * rad * toUv, edge, 1.0 - edge);
  return vec4(cinematicGraded(cinematicExposed(texture2D(tDiffuse, at).rgb)),
    length((at - lensFlareDiscUv) / toUv));
}

// The limb's own light round a disc that shows its surface, on this pixel's
// line from the disc's centre (dp, in the glare's measure): the frame two
// and a half pixels inside the edge, so the glow takes the limb's colour,
// spots and whatever covers it. Where that point is off the frame the
// nearest point on it stands in while it lies on the disc, and the glow
// goes where none does.
vec3 sgLimbLight(vec2 dp) {
  float px = 1.0 / max(lensFlareViewPx, 1.0);
  float limb = lensFlareDisc.x / max(lensFlareDegPerUv, 1e-6);
  vec4 inside = sgRadialLight(dp / max(length(dp), 1e-6), max(limb - 2.5 * px, 0.0));
  return inside.rgb * (1.0 - smoothstep(limb - 1.5 * px, limb - 0.5 * px, inside.w));
}

// What the lens adds at this pixel, in linear light. lit: the share of the
// Sun's light that reaches the lens; pixVis: spacecraft over this pixel.
vec3 sunGlare(vec2 uv, float lit, float pixVis) {
  if (lensFlareSpace <= 0.001 || lit <= 0.0) return vec3(0.0);
  vec2 sp = vec2((uv.x - lensFlareSun.x) * lensFlareAspect, uv.y - lensFlareSun.y);
  float scale = max(lensFlareScale, 1.0);
  float pxDeg = lensFlareDegPerUv / max(lensFlareViewPx, 1.0);
  float th = length(sp) * lensFlareDegPerUv / scale;
  float amount = lensFlareSpace * lit;
  float blowout = lensFlareDisc.y;
  // The rays and streak go before the disc's edge shows through the core,
  // so a resolved disc never wears them.
  float pointLike = smoothstep(0.55, 0.95, blowout);

  // Over a disc that shows its surface only what lies past its limb, and
  // the glow is measured from the limb, which it hugs, where round a point
  // it is measured from the light's centre. Its edge comes in over the
  // last pixel and a quarter inside the limb, so the limb's own
  // antialiased pixel, half disc, takes the whole glow: ramped across the
  // limb it took half, and that pixel drew a dark line.
  float under = 1.0;
  // The glow is enlarged round an artistic disc by its own share of the
  // enlargement (lensFlareGlowScale), so a wide glow need not fog the frame.
  float glowScale = max(lensFlareGlowScale, 1.0);
  float thGlow = length(sp) * lensFlareDegPerUv / glowScale;
  vec3 limbLight = vec3(0.0);
  if (blowout < 1.0) {
    vec2 dp = vec2((uv.x - lensFlareDiscUv.x) * lensFlareAspect, uv.y - lensFlareDiscUv.y);
    float rawDisc = length(dp) * lensFlareDegPerUv;
    float past = smoothstep(lensFlareDisc.x - 1.5 * pxDeg, lensFlareDisc.x - 0.25 * pxDeg, rawDisc);
    under = mix(past, 1.0, blowout);
    thGlow = mix(max(rawDisc - lensFlareDisc.x, 0.0) / glowScale, thGlow, blowout);
    if (under > 0.0) limbLight = sgLimbLight(dp);
  }

  // The core, off the silhouette of the body cutting the disc (its glow and
  // rays lie over it, as the lens spreads them).
  float coreClear = 1.0;
  if (lensFlareBodyMask > 0.0) {
    // Through the near plane: the far plane is so far out that its w is
    // about zero.
    vec4 nearPoint = lensFlareInvProj * vec4(uv * 2.0 - 1.0, -1.0, 1.0);
    vec3 viewRay = normalize(nearPoint.xyz / nearPoint.w);
    float edge = sqrt(max(1.0 - lensFlareBody.w * lensFlareBody.w, 0.0)) * pxDeg * 0.0174533;
    float cover = smoothstep(lensFlareBody.w - edge, lensFlareBody.w + edge, dot(viewRay, lensFlareBody.xyz));
    coreClear = 1.0 - cover * lensFlareBodyMask;
  }
  float thCore = th;
  if (lensFlareRefract > 0.0) {
    vec2 su = sgSourceUv(uv);
    thCore = length(vec2((su.x - lensFlareSunSrc.x) * lensFlareAspect, su.y - lensFlareSunSrc.y)) * lensFlareDegPerUv / scale;
  }
  float a = lensFlareShape.x;
  float q = 1.0 + thCore * thCore / (a * a);
  float core = lensFlareGain / (q * sqrt(q)) * pixVis * coreClear * blowout;
  // Its tail is cut off a little past its width: at the low light where
  // it would linger, warm light shows brown under the tone curve.
  float g = lensFlareShape.y;
  float qg = 1.0 + thGlow * thGlow / (g * g);
  float glowShape = 1.0 / (qg * sqrt(qg)) * exp(-thGlow / (lensFlareGlowEnd * g)) * under;
  // Round a point, the lens's glow; round a disc that shows its surface,
  // the limb's own light spread past it.
  float glow = lensFlareGlow.x * glowShape * blowout;
  // Round a disc that shows its surface the glow reaches lensFlareLimbGlow
  // of the limb's light at the edge, and joins what the frame already holds
  // at this pixel (the bloom, a prominence, a star, the limb's own
  // antialiased pixel) as the larger of the two, rounded where they meet
  // (a polynomial smooth maximum, at most 4% over the larger): the two
  // together stay under the limb, where a plain sum outshone it and drew a
  // dark ring round the disc, and nothing brighter than the glow loses any
  // of its light. sqrt(glow^2 + frame^2) rounded too much, up to 1.41 times
  // the larger, and lit the limb's own pixel as a bright line. By
  // brightness, in the limb's colour.
  vec3 limbGlow = vec3(0.0);
  vec3 limbTarget = lensFlareLimbGlow * limbLight * glowShape * (1.0 - blowout);
  vec3 sgLuma = vec3(0.2126, 0.7152, 0.0722);
  float tLum = dot(limbTarget, sgLuma);
  if (tLum > 1e-5) {
    float bLum = dot(cinematicGraded(cinematicExposed(texture2D(tDiffuse, uv).rgb)), sgLuma);
    float most = max(tLum, bLum);
    float k = 0.15 * most;
    float h = max(k - abs(tLum - bLum), 0.0) / max(k, 1e-6);
    limbGlow = limbTarget * ((most + 0.25 * k * h * h - bLum) / tLum);
  }
  // The veil is enlarged only by the root of the enlargement, so round a
  // large artistic disc it does not fog the frame.
  float thFar = length(sp) * lensFlareDegPerUv / max(lensFlareFarScale, 1.0);
  float v = lensFlareShape.z;
  float veil = lensFlareGlow.y / (1.0 + thFar * thFar / (v * v)) * exp(-thFar / lensFlareShape.w) * under * blowout;

  // The rays: the nearest two, so where neighbours overlap near the core
  // their light adds without a seam.
  float rays = 0.0;
  float w = lensFlareRayShape.x;
  // Narrower than a pixel a profile is widened to one, its peak lowered to
  // keep its light.
  float wFloor = 0.7 * pxDeg / scale;
  if (lensFlareSpikes > 0.001) {
    float gap = 2.0 * SG_PI / max(lensFlareRayCount, 2.0);
    float ang = atan(sp.y, sp.x) - lensFlareRayTurn;
    float k = floor(ang / gap + 0.5);
    float off = ang - k * gap;
    float k2 = k + (off >= 0.0 ? 1.0 : -1.0);
    float off2 = ang - k2 * gap;
    float wDraw = max(w, wFloor);
    rays = lensFlareSpikes * pointLike * under
      * (sgRay(k, off, th, wDraw, w / wDraw) + sgRay(k2, off2, th, wDraw, w / wDraw));
  }
  // The strands: the nearest three, since a jittered one can stand nearer
  // than its even place, and none past where the longest has faded.
  if (lensFlareStrands > 0.001 && th < 7.0 * lensFlareSpikeLength * lensFlareStrandShape.y) {
    float gap = 2.0 * SG_PI / max(floor(lensFlareStrandShape.x + 0.5), 3.0);
    float ang = atan(sp.y, sp.x) - lensFlareRayTurn;
    float j = floor(ang / gap);
    rays += lensFlareStrands * pointLike * under
      * (sgStrand(j - 1.0, ang, th, gap, w, wFloor) + sgStrand(j, ang, th, gap, w, wFloor) + sgStrand(j + 1.0, ang, th, gap, w, wFloor));
  }

  // The streak: about a pixel high, fading along the frame.
  float streak = 0.0;
  vec3 streakTint = lensFlareWarm;
  if (lensFlareStreak > 0.001) {
    float dx = abs(uv.x - lensFlareSun.x) * lensFlareAspect;
    float dyPx = (uv.y - lensFlareSun.y) * lensFlareViewPx;
    float along = dx / 0.55;
    float h = 1.0 + dyPx * dyPx / 0.8;
    streak = lensFlareStreak * pointLike * exp(-along * 2.6) * (1.0 / h + 0.05 * exp(-abs(dyPx) / 7.0));
    streakTint = mix(lensFlareWarm, vec3(0.42, 0.62, 1.0), smoothstep(0.08, 0.9, along));
  }

  // The ghosts, on the axis through the middle of the frame. With the Sun
  // near the middle they stack on it as rings round the glare, so they go.
  float ghostLight = 0.0;
  vec3 ghostTint = vec3(0.0);
  vec2 axis = vec2(0.5) - lensFlareSun;
  float offCentre = smoothstep(0.06, 0.22, length(vec2(axis.x * lensFlareAspect, axis.y)));
  if (lensFlareGhosts * offCentre > 0.0001) {
    float px = 1.0 / max(lensFlareViewPx, 1.0);
    float g1 = sgGhost(uv, lensFlareSun + axis * 0.38, 0.016, 2.0 * px / 0.016);
    float g2 = sgGhost(uv, lensFlareSun + axis * 0.66, 0.046, 3.0 * px / 0.046);
    float g3 = sgGhost(uv, lensFlareSun + axis * 0.9, 0.011, 2.0 * px / 0.011);
    float g4 = sgGhost(uv, lensFlareSun + axis * 1.22, 0.072, 4.0 * px / 0.072);
    float g5 = sgGhost(uv, lensFlareSun + axis * 1.48, 0.028, 2.5 * px / 0.028);
    float g6 = sgGhost(uv, lensFlareSun - axis * 0.2, 0.021, 2.0 * px / 0.021);
    ghostTint = vec3(1.0, 0.62, 0.3) * g1 * 0.9
      + vec3(0.5, 0.72, 1.0) * g2 * 0.42
      + vec3(0.95, 0.5, 0.88) * g3 * 0.8
      + vec3(0.48, 0.9, 0.74) * g4 * 0.2
      + vec3(1.0, 0.58, 0.32) * g5 * 0.5
      + vec3(0.6, 0.7, 1.0) * g6 * 0.45;
    ghostLight = lensFlareGhosts * offCentre;
  }

  // The core is the Sun's own white; the glow and rays take the lens's
  // colour (lensFlareWarm, white by default, as the Sun is from space).
  vec3 white = vec3(1.0);
  vec3 light = white * core
    + lensFlareWarm * glow
    + mix(lensFlareWarm, white, 0.65) * veil
    + lensFlareRayTint * rays
    + streakTint * streak;
  // A few times past white is white: the cap only keeps the antialiasing
  // passes after this one from meeting huge values.
  light = min(light * amount, vec3(24.0)) + ghostTint * ghostLight * lit;
  // The limb already carries the air's tint and whatever covers it.
  return lensFlareTint * light + limbGlow * amount;
}
`;
